import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import { Building2, LogOut, ShoppingCart, ClipboardList, Plus, Minus, ShieldCheck, Copy, UserPlus, Trash2, Wallet, TrendingUp, CheckCircle2, Clock } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

export default function Wholesale() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("shop");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState({});   // key = product_id::sku → { qty, unit_price, min_qty, name, label }
  const [checkout, setCheckout] = useState(false);
  const [payment, setPayment] = useState("razorpay");
  const [utr, setUtr] = useState("");
  const [notes, setNotes] = useState("");
  const [bank, setBank] = useState(null);

  // Reseller customer-order form
  const emptyCustomerLine = () => ({ product_id: "", variant_sku: "", qty: 1 });
  const emptyCustomerForm = () => ({
    customer_name: "", customer_phone: "", customer_email: "",
    address: { line1: "", city: "", state: "", pincode: "", country: "India" },
    items: [emptyCustomerLine()],
    notes: "", payment_method: "bank_transfer", bank_utr: "",
  });
  const [cForm, setCForm] = useState(emptyCustomerForm());
  const [placingCustomer, setPlacingCustomer] = useState(false);

  // Earnings
  const [earnings, setEarnings] = useState(null);
  useEffect(() => {
    if (tab === "earnings") api.get("/wholesale/earnings").then(r => setEarnings(r.data)).catch(()=>{});
  }, [tab]);

  useEffect(() => {
    api.get("/wholesale/me").then(r=>{
      setMe(r.data); refresh();
      api.get("/wholesale/company-bank").then(b=>setBank(b.data)).catch(()=>{});
    }).catch(() => nav("/wholesale/login"));
  }, []);

  const refresh = () => {
    api.get("/wholesale/products").then(r=>setProducts(r.data)).catch(()=>{});
    api.get("/wholesale/orders").then(r=>setOrders(r.data)).catch(()=>{});
  };

  const setQty = (p, v, qty) => {
    const k = `${p.product_id}::${v.sku}`;
    setCart(prev => {
      const next = { ...prev };
      if (qty <= 0) delete next[k];
      else next[k] = { qty, unit_price: v.price, retail_price: v.retail_price,
                       min_qty: v.min_qty, product_id: p.product_id, sku: v.sku,
                       name: p.name, label: v.label, image: (p.images||[])[0] };
      return next;
    });
  };

  const cartRows = useMemo(() => Object.values(cart), [cart]);
  const cartTotal = cartRows.reduce((s,r) => s + r.qty * r.unit_price, 0);
  const cartCount = cartRows.reduce((s,r) => s + r.qty, 0);

  const logout = async () => { try { await api.post("/auth/logout"); } catch{} nav("/wholesale/login"); };

  const placeOrder = async () => {
    for (const r of cartRows) if (r.qty < r.min_qty) return toast.error(`${r.name} (${r.label}) requires min ${r.min_qty} units`);
    if (cartRows.length === 0) return toast.error("Cart is empty");
    if (payment === "bank_transfer" && !utr.trim()) return toast.error("Enter the UTR reference from your bank transfer");
    try {
      const r = await api.post("/wholesale/orders", {
        items: cartRows.map(r=>({ product_id: r.product_id, variant_sku: r.sku, qty: r.qty })),
        payment_method: payment, bank_utr: utr, notes,
      });
      toast.success(`Order ${r.data.order_no} placed`);
      if (payment === "razorpay") {
        window.location.href = `/pay/${r.data.order_id}`;
        return;
      }
      setCart({}); setCheckout(false); setUtr(""); setNotes(""); refresh(); setTab("orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to place order"); }
  };

  const placeCustomerOrder = async () => {
    const f = cForm;
    if (!f.customer_name.trim()) return toast.error("Customer name is required");
    if (!f.customer_phone.trim()) return toast.error("Customer phone is required");
    for (const k of ["line1", "city", "state", "pincode"]) {
      if (!f.address[k]?.trim()) return toast.error(`Delivery address: ${k} is required`);
    }
    if (f.items.some(l => !l.product_id || !l.variant_sku || !(l.qty > 0))) {
      return toast.error("Pick a product + variant + qty on every line");
    }
    if (f.payment_method === "bank_transfer" && !f.bank_utr.trim()) {
      return toast.error("Enter the UTR / transaction reference");
    }
    setPlacingCustomer(true);
    try {
      const r = await api.post("/wholesale/customer-orders", f);
      toast.success(`Order ${r.data.order_no} placed for ${f.customer_name}`);
      setCForm(emptyCustomerForm()); refresh(); setTab("orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to place order"); }
    finally { setPlacingCustomer(false); }
  };

  const customerSubtotal = useMemo(() => {
    return cForm.items.reduce((s, line) => {
      const p = products.find(x => x.product_id === line.product_id);
      const v = p?.variants?.find(vx => vx.sku === line.variant_sku);
      return s + (v ? Number(v.price || 0) * Number(line.qty || 0) : 0);
    }, 0);
  }, [cForm.items, products]);

  if (!me) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-gradient-to-r from-[#3B2F2F] to-[#2A2020] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#D4AF37] text-[#3B2F2F] flex items-center justify-center font-bold">DB</div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Seller portal</div>
              <div className="font-outfit font-bold">{me.business_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1 text-xs">
              {me.gstin ? <><ShieldCheck className="w-3 h-3 text-[#D4AF37]"/> GSTIN {me.gstin}</> : <span className="text-white/50">GSTIN not set</span>}
            </div>
            <button onClick={logout} data-testid="wh-logout" className="text-xs bg-white/10 rounded-full px-3 py-1.5 hover:bg-white/20 flex items-center gap-1"><LogOut className="w-3 h-3"/> Sign out</button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex gap-2 pb-3">
          {[{k:"shop",l:"Shop",i:Building2},{k:"cart",l:`Cart (${cartCount})`,i:ShoppingCart},{k:"customer",l:"New customer order",i:UserPlus},{k:"orders",l:"My orders",i:ClipboardList},{k:"earnings",l:"Earnings",i:Wallet}].map(t=>(
            <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`wh-tab-${t.k}`}
                    className={`text-sm rounded-full px-4 py-1.5 flex items-center gap-1.5 ${tab===t.k?"bg-[#D4AF37] text-[#3B2F2F] font-semibold":"bg-white/10 text-white hover:bg-white/20"}`}>
              <t.i className="w-3.5 h-3.5"/> {t.l}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {tab === "shop" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.length === 0 && <div className="col-span-full text-center text-stone-500 py-16">No products priced for wholesale yet. Ask the admin to add wholesale prices.</div>}
            {products.map(p => (
              <div key={p.product_id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                <div className="aspect-video bg-stone-100 relative">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover"/>}
                </div>
                <div className="p-4">
                  <div className="font-outfit font-semibold">{p.name}</div>
                  <div className="text-xs text-stone-500 line-clamp-2 mt-1">{p.description}</div>
                  <div className="space-y-2 mt-3">
                    {p.variants.map(v => {
                      const k = `${p.product_id}::${v.sku}`;
                      const q = cart[k]?.qty || 0;
                      return (
                        <div key={v.sku} className="flex items-center gap-2 border-t border-stone-100 pt-2">
                          <div className="flex-1">
                            <div className="text-xs text-stone-500">{v.label} · min {v.min_qty}</div>
                            <div className="flex items-baseline gap-2">
                              <span className="font-outfit font-bold text-[#3B2F2F]">{inr(v.price)}</span>
                              <span className="text-[11px] text-stone-400 line-through">{inr(v.retail_price)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 border border-stone-200 rounded-full px-1">
                            <button onClick={()=>setQty(p, v, Math.max(0, q - v.min_qty))} className="w-6 h-6 text-stone-500 hover:text-[#3B2F2F]"><Minus className="w-3 h-3 mx-auto"/></button>
                            <input type="number" min={0} value={q} onChange={e=>setQty(p, v, Number(e.target.value))}
                                   data-testid={`wh-qty-${p.slug}-${v.sku}`}
                                   className="w-14 text-center text-sm bg-transparent focus:outline-none"/>
                            <button onClick={()=>setQty(p, v, q + v.min_qty)} className="w-6 h-6 text-stone-500 hover:text-[#3B2F2F]"><Plus className="w-3 h-3 mx-auto"/></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "cart" && (
          <div className="max-w-3xl mx-auto bg-white border border-stone-200 rounded-2xl p-6">
            <h2 className="font-serif-display text-3xl mb-4">Your cart</h2>
            {cartRows.length === 0 ? (
              <div className="text-stone-500 py-10 text-center">Cart is empty. Head back to Shop to add items.</div>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {cartRows.map(r => (
                    <div key={`${r.product_id}::${r.sku}`} className="flex items-center gap-3 border-b border-stone-100 pb-2">
                      {r.image && <img src={r.image} className="w-14 h-14 rounded-lg object-cover"/>}
                      <div className="flex-1">
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-stone-500">{r.label} · {inr(r.unit_price)} × {r.qty}</div>
                      </div>
                      <div className="font-outfit font-semibold">{inr(r.qty * r.unit_price)}</div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center font-outfit font-bold text-xl border-t pt-3">
                  <span>Total</span><span data-testid="wh-cart-total" className="text-[#3B2F2F]">{inr(cartTotal)}</span>
                </div>
                <button onClick={()=>setCheckout(true)} data-testid="wh-checkout-btn"
                        className="mt-4 w-full bg-[#3B2F2F] text-white rounded-md py-2.5 font-medium hover:bg-black">
                  Proceed to checkout
                </button>
              </>
            )}
          </div>
        )}

        {tab === "customer" && (
          <div className="max-w-4xl mx-auto bg-white border border-stone-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-serif-display text-3xl flex items-center gap-2">
                  <UserPlus className="w-6 h-6 text-[#D4AF37]"/> New customer order
                </h2>
                <p className="text-sm text-stone-500 mt-1">
                  Place an order on behalf of your customer. DailyBeans will ship + invoice directly to them at your wholesale rates.
                </p>
              </div>
            </div>

            <section className="mb-6">
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Customer details</div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Customer name *" value={cForm.customer_name}
                        onChange={e=>setCForm(f=>({...f, customer_name: e.target.value}))}
                        data-testid="co-customer-name"/>
                <Input placeholder="Customer phone *" value={cForm.customer_phone}
                        onChange={e=>setCForm(f=>({...f, customer_phone: e.target.value}))}
                        data-testid="co-customer-phone"/>
                <Input className="col-span-2" placeholder="Customer email (optional)"
                        value={cForm.customer_email}
                        onChange={e=>setCForm(f=>({...f, customer_email: e.target.value}))}/>
              </div>
            </section>

            <section className="mb-6">
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Delivery address</div>
              <div className="grid grid-cols-2 gap-3">
                <Input className="col-span-2" placeholder="Street address *"
                        value={cForm.address.line1}
                        onChange={e=>setCForm(f=>({...f, address: {...f.address, line1: e.target.value}}))}
                        data-testid="co-line1"/>
                <Input placeholder="City *" value={cForm.address.city}
                        onChange={e=>setCForm(f=>({...f, address: {...f.address, city: e.target.value}}))}/>
                <Input placeholder="State *" value={cForm.address.state}
                        onChange={e=>setCForm(f=>({...f, address: {...f.address, state: e.target.value}}))}/>
                <Input placeholder="Pincode *" value={cForm.address.pincode}
                        onChange={e=>setCForm(f=>({...f, address: {...f.address, pincode: e.target.value}}))}/>
                <Input placeholder="Country" value={cForm.address.country}
                        onChange={e=>setCForm(f=>({...f, address: {...f.address, country: e.target.value}}))}/>
              </div>
            </section>

            <section className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-stone-500">Items</div>
                <button onClick={()=>setCForm(f=>({...f, items: [...f.items, emptyCustomerLine()]}))}
                        data-testid="co-add-line"
                        className="text-xs text-[#3B2F2F] hover:underline flex items-center gap-1">
                  <Plus className="w-3 h-3"/> Add row
                </button>
              </div>
              {products.length === 0 && (
                <div className="text-sm text-stone-500 italic">No wholesale-priced products available. Ask the admin to set wholesale prices.</div>
              )}
              <div className="space-y-2">
                {cForm.items.map((line, i) => {
                  const prod = products.find(p => p.product_id === line.product_id);
                  const vari = prod?.variants?.find(v => v.sku === line.variant_sku);
                  const lineTotal = vari ? Number(vari.price || 0) * Number(line.qty || 0) : 0;
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-stone-50 rounded-lg p-2">
                      <select className="col-span-5 bg-white border border-stone-200 rounded-md px-2 py-1.5 text-sm"
                              value={line.product_id}
                              onChange={e=>setCForm(f=>({ ...f, items: f.items.map((x,idx) => idx===i ? {...x, product_id: e.target.value, variant_sku: ""} : x) }))}
                              data-testid={`co-line-${i}-product`}>
                        <option value="">— pick product —</option>
                        {products.map(p => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                      </select>
                      <select className="col-span-3 bg-white border border-stone-200 rounded-md px-2 py-1.5 text-sm"
                              value={line.variant_sku}
                              disabled={!prod}
                              onChange={e=>setCForm(f=>({ ...f, items: f.items.map((x,idx) => idx===i ? {...x, variant_sku: e.target.value} : x) }))}
                              data-testid={`co-line-${i}-variant`}>
                        <option value="">— Wt —</option>
                        {prod?.variants?.map(v => <option key={v.sku} value={v.sku}>{v.label} · {inr(v.price)} (min {v.min_qty})</option>)}
                      </select>
                      <Input className="col-span-2" type="number" min={1}
                              placeholder="Qty"
                              value={line.qty}
                              onChange={e=>setCForm(f=>({ ...f, items: f.items.map((x,idx) => idx===i ? {...x, qty: Number(e.target.value)} : x) }))}
                              data-testid={`co-line-${i}-qty`}/>
                      <div className="col-span-1 text-right font-outfit text-sm">{inr(lineTotal)}</div>
                      <button onClick={()=>setCForm(f=>({ ...f, items: f.items.filter((_,idx)=>idx!==i) }))}
                              disabled={cForm.items.length === 1}
                              className="col-span-1 text-red-500 disabled:text-stone-300 flex justify-center">
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-3 font-outfit font-bold text-lg">
                <span className="text-stone-500 mr-3">Order total</span>
                <span className="text-[#3B2F2F]" data-testid="co-total">{inr(customerSubtotal)}</span>
              </div>
            </section>

            <section className="mb-6">
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Payment</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {[
                  { k: "bank_transfer", l: "Bank transfer" },
                  { k: "razorpay", l: "Pay online" },
                  { k: "cod", l: "COD" },
                  { k: "prepaid", l: "Already paid" },
                ].map(opt => (
                  <button key={opt.k}
                    onClick={()=>setCForm(f=>({...f, payment_method: opt.k}))}
                    data-testid={`co-pay-${opt.k}`}
                    className={`text-sm border rounded-lg px-3 py-2 ${
                      cForm.payment_method === opt.k ? "bg-[#3B2F2F] text-white border-[#3B2F2F]" : "bg-white border-stone-200"
                    }`}>{opt.l}</button>
                ))}
              </div>
              {cForm.payment_method === "bank_transfer" && (
                <Input placeholder="UTR / Transaction reference *"
                        value={cForm.bank_utr}
                        onChange={e=>setCForm(f=>({...f, bank_utr: e.target.value}))}
                        data-testid="co-utr"/>
              )}
            </section>

            <Textarea placeholder="Notes for the packing team (optional)"
                      value={cForm.notes}
                      onChange={e=>setCForm(f=>({...f, notes: e.target.value}))}/>

            <div className="flex justify-end gap-2 mt-5 pt-5 border-t">
              <button onClick={()=>setCForm(emptyCustomerForm())}
                      className="border border-stone-300 rounded-full px-5 py-2 text-sm hover:bg-stone-50">
                Clear
              </button>
              <button onClick={placeCustomerOrder} disabled={placingCustomer || products.length === 0}
                      data-testid="co-submit"
                      className="bg-[#3B2F2F] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-black disabled:opacity-60 flex items-center gap-2">
                <UserPlus className="w-4 h-4"/>
                {placingCustomer ? "Placing…" : `Submit order · ${inr(customerSubtotal)}`}
              </button>
            </div>
          </div>
        )}

        {tab === "earnings" && (
          <div className="max-w-5xl mx-auto">
            <h2 className="font-serif-display text-3xl mb-5 flex items-center gap-2">
              <Wallet className="w-6 h-6 text-[#D4AF37]"/> Earnings
            </h2>
            {!earnings ? <div className="text-stone-500">Loading…</div> : (<>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <EKpi icon={ClipboardList} label="Orders placed" value={earnings.summary.orders_total} accent="#3B2F2F"/>
                <EKpi icon={TrendingUp} label="Order value (GMV)" value={inr(earnings.summary.gmv)} accent="#D4AF37"/>
                <EKpi icon={Clock} label="Payouts pending" value={inr(earnings.summary.payout_pending)} accent="#8B5E3C"/>
                <EKpi icon={CheckCircle2} label="Payouts received" value={inr(earnings.summary.payout_paid)} accent="#2E4A35"/>
              </div>
              <div className="bg-white border border-stone-200 rounded-2xl p-5">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">Latest orders</div>
                {earnings.recent_orders.length === 0 ? (
                  <div className="text-sm text-stone-400 italic">No orders yet. Place your first customer order from the "New customer order" tab.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] uppercase tracking-widest text-stone-500 border-b">
                      <th className="pb-2">Order #</th><th>Date</th><th>Customer</th>
                      <th className="text-right">Total</th><th className="text-right">Your payout</th><th className="text-center">Status</th>
                    </tr></thead>
                    <tbody>
                      {earnings.recent_orders.map(o => (
                        <tr key={o.order_no} className="border-b last:border-0 hover:bg-stone-50">
                          <td className="py-2 font-medium">{o.order_no}</td>
                          <td className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString()}</td>
                          <td className="text-xs">{o.customer_name || "—"}</td>
                          <td className="text-right font-outfit">{inr(o.total)}</td>
                          <td className="text-right font-outfit text-[#8B5E3C]">
                            {Number(o.reseller_payout || 0) > 0 ? inr(o.reseller_payout) : <span className="text-stone-300">—</span>}
                          </td>
                          <td className="text-center">
                            {o.settlement_status === "paid"
                              ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E8EDDF] text-[#2E4A35] uppercase tracking-widest">Paid</span>
                              : Number(o.reseller_payout || 0) > 0
                                ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF3E2] text-[#8B5E3C] uppercase tracking-widest">Pending</span>
                                : <span className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-400 uppercase tracking-widest">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>)}
          </div>
        )}

        {tab === "orders" && (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500">
                <tr><th className="p-3 text-left">Order</th><th className="p-3 text-left">Date</th><th className="p-3 text-left">Items</th><th className="p-3 text-right">Total</th><th className="p-3">Status</th><th className="p-3">Invoice</th></tr>
              </thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={6} className="text-center text-stone-500 py-10">No orders yet.</td></tr>}
                {orders.map(o => (
                  <tr key={o.order_id} className="border-t border-stone-100" data-testid={`wh-order-${o.order_no}`}>
                    <td className="p-3 font-medium">{o.order_no}</td>
                    <td className="p-3 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="p-3 text-xs">{o.items.length} lines · {o.items.reduce((s,i)=>s+i.qty,0)} units</td>
                    <td className="p-3 text-right font-outfit font-semibold">{inr(o.total)}</td>
                    <td className="p-3 text-xs uppercase text-center">
                      <span className={`px-2 py-1 rounded-full ${o.payment_status==="paid"?"bg-[#E8EDDF] text-[#2E4A35]":"bg-[#FEF3E2] text-[#8B7355]"}`}>
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.order_id}/invoice.pdf`} target="_blank" rel="noreferrer"
                         className="text-[#D4AF37] hover:underline text-xs">Download</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Checkout dialog */}
      {checkout && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={()=>setCheckout(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e=>e.stopPropagation()}>
            <h3 className="font-serif-display text-2xl mb-4">Confirm order</h3>
            <div className="bg-[#FDFBF7] border border-stone-200 rounded-lg p-3 text-sm mb-4">
              <div>{cartCount} units · {inr(cartTotal)}</div>
              <div className="text-xs text-stone-500 mt-1">Shipping absorbed. GSTIN {me.gstin} on invoice.</div>
            </div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Payment method</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {["razorpay","bank_transfer"].map(m=>(
                <button key={m} onClick={()=>setPayment(m)} data-testid={`wh-pay-${m}`}
                        className={`text-sm border rounded-lg px-3 py-2 ${payment===m?"bg-[#3B2F2F] text-white border-[#3B2F2F]":"bg-white border-stone-200"}`}>
                  {m === "razorpay" ? "Pay online (Razorpay)" : "Bank transfer (NEFT/RTGS)"}
                </button>
              ))}
            </div>
            {payment === "bank_transfer" && (
              <div className="space-y-3 mb-4">
                {bank?.configured ? (
                  <div className="bg-[#FEF3E2] border border-[#D4AF37]/40 rounded-lg p-3 text-xs space-y-1 font-mono">
                    <div><b>Bank:</b> {bank.bank_name}</div>
                    <div><b>A/c name:</b> {bank.account_name}</div>
                    <div className="flex items-center gap-2"><b>A/c number:</b> {bank.account_number} <button onClick={()=>{navigator.clipboard.writeText(bank.account_number); toast.success("Copied");}}><Copy className="w-3 h-3"/></button></div>
                    <div><b>IFSC:</b> {bank.ifsc}</div>
                    {bank.upi_id && <div><b>UPI:</b> {bank.upi_id}</div>}
                  </div>
                ) : (
                  <div className="text-xs text-red-600">Bank details not configured yet. Ask admin.</div>
                )}
                <Input placeholder="UTR / Transaction reference" value={utr} onChange={e=>setUtr(e.target.value)} data-testid="wh-utr"/>
              </div>
            )}
            <Textarea placeholder="Notes for the packing team (optional)" value={notes} onChange={e=>setNotes(e.target.value)}/>
            <div className="flex gap-2 mt-4">
              <button onClick={()=>setCheckout(false)} className="flex-1 border border-stone-300 rounded-md py-2 text-sm">Cancel</button>
              <button onClick={placeOrder} data-testid="wh-place-order"
                      className="flex-1 bg-[#3B2F2F] text-white rounded-md py-2 text-sm hover:bg-black">
                {payment === "razorpay" ? "Continue to payment" : "Submit order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EKpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500">
        {Icon && <Icon className="w-3.5 h-3.5"/>} {label}
      </div>
      <div className="font-outfit font-bold text-2xl mt-1" style={{color: accent}}>{value}</div>
    </div>
  );
}
